/**
 * DamageDisplayQueue
 *
 * Manages queued damage displays with configurable pacing.
 * Ensures damage numbers don't appear all at once when multiple
 * events arrive in the same frame.
 *
 * Part of the Action-Based Combat Visualization system.
 */

import type { ReadyDamage } from './action-correlation-service';

/**
 * Configuration for damage display queue
 */
export interface DamageDisplayQueueConfig {
  /** Minimum interval between displaying damages on same target (ms) */
  minIntervalPerTarget: number;
  /** Maximum queue depth before force-releasing oldest */
  maxQueueDepth: number;
  /** Maximum age before force-releasing (ms) */
  maxQueueAge: number;
}

/**
 * Default configuration
 */
export const DEFAULT_QUEUE_CONFIG: DamageDisplayQueueConfig = {
  minIntervalPerTarget: 50,   // 50ms between damages on same target
  maxQueueDepth: 50,          // Max 50 damages queued
  maxQueueAge: 1000           // Force release after 1 second
};

/**
 * Queued damage entry
 */
interface QueuedDamage {
  damage: ReadyDamage;
  enqueuedAt: number;
  releaseTime: number;
}

/**
 * DamageDisplayQueue
 *
 * Manages pacing of damage number displays to prevent visual chaos
 * when many damages arrive simultaneously.
 */
export class DamageDisplayQueue {
  private config: DamageDisplayQueueConfig;
  private queue: QueuedDamage[] = [];
  private lastDisplayTimeByTarget: Map<string, number> = new Map();

  constructor(config: Partial<DamageDisplayQueueConfig> = {}) {
    this.config = { ...DEFAULT_QUEUE_CONFIG, ...config };
  }

  /**
   * Update configuration
   */
  public configure(config: Partial<DamageDisplayQueueConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Enqueue a damage for display
   * @param damage The damage to display
   * @param releaseTime When to release (0 = ASAP with pacing)
   */
  public enqueue(damage: ReadyDamage, releaseTime: number = 0): void {
    const now = performance.now();

    // Calculate actual release time considering pacing
    // Check both last displayed AND last scheduled release for this target
    const lastDisplay = this.lastDisplayTimeByTarget.get(damage.victimId) || 0;
    const lastScheduled = this.getLastScheduledReleaseForTarget(damage.victimId);
    const lastRelevantTime = Math.max(lastDisplay, lastScheduled);

    const earliestRelease = Math.max(
      releaseTime,
      lastRelevantTime > 0 ? lastRelevantTime + this.config.minIntervalPerTarget : releaseTime
    );

    const queuedDamage: QueuedDamage = {
      damage,
      enqueuedAt: now,
      releaseTime: earliestRelease
    };

    this.queue.push(queuedDamage);

    // Enforce max queue depth
    if (this.queue.length > this.config.maxQueueDepth) {
      // Force release oldest
      this.queue.shift();
    }
  }

  /**
   * Get the latest scheduled release time for a target from queued damages
   */
  private getLastScheduledReleaseForTarget(targetId: string): number {
    let maxTime = 0;
    for (const queued of this.queue) {
      if (queued.damage.victimId === targetId && queued.releaseTime > maxTime) {
        maxTime = queued.releaseTime;
      }
    }
    return maxTime;
  }

  /**
   * Enqueue multiple damages at once
   * Automatically staggers their release times
   */
  public enqueueMultiple(damages: ReadyDamage[], baseReleaseTime: number = 0): void {
    // Group by target for proper pacing
    const byTarget = new Map<string, ReadyDamage[]>();
    for (const damage of damages) {
      const existing = byTarget.get(damage.victimId) || [];
      existing.push(damage);
      byTarget.set(damage.victimId, existing);
    }

    // Enqueue with staggered timing per target
    for (const [targetId, targetDamages] of byTarget) {
      let releaseTime = baseReleaseTime;
      for (const damage of targetDamages) {
        this.enqueue(damage, releaseTime);
        releaseTime += this.config.minIntervalPerTarget;
      }
    }
  }

  /**
   * Get damages that are ready for display
   */
  public getReadyDamages(currentTime: number = performance.now()): ReadyDamage[] {
    const ready: ReadyDamage[] = [];
    const remaining: QueuedDamage[] = [];

    for (const queued of this.queue) {
      // Check if ready (release time passed)
      const isTimeReady = currentTime >= queued.releaseTime;

      // Check if aged out (force release)
      const age = currentTime - queued.enqueuedAt;
      const isAgedOut = age >= this.config.maxQueueAge;

      if (isTimeReady || isAgedOut) {
        ready.push(queued.damage);
        // Update last display time for this target
        this.lastDisplayTimeByTarget.set(queued.damage.victimId, currentTime);
      } else {
        remaining.push(queued);
      }
    }

    this.queue = remaining;
    return ready;
  }

  /**
   * Force release all pending damages
   */
  public releaseAll(): ReadyDamage[] {
    const damages = this.queue.map(q => q.damage);
    this.queue = [];
    return damages;
  }

  /**
   * Get number of queued damages
   */
  public getQueueLength(): number {
    return this.queue.length;
  }

  /**
   * Check if queue is empty
   */
  public isEmpty(): boolean {
    return this.queue.length === 0;
  }

  /**
   * Clear all queued damages without releasing
   */
  public clear(): void {
    this.queue = [];
    this.lastDisplayTimeByTarget.clear();
  }

  /**
   * Set minimum interval between damages on same target
   */
  public setMinIntervalMs(ms: number): void {
    this.config.minIntervalPerTarget = ms;
  }

  /**
   * Set maximum queue depth
   */
  public setMaxQueueDepth(depth: number): void {
    this.config.maxQueueDepth = depth;
  }

  /**
   * Get queued damages for a specific target (for debugging)
   */
  public getQueuedForTarget(targetId: string): ReadyDamage[] {
    return this.queue
      .filter(q => q.damage.victimId === targetId)
      .map(q => q.damage);
  }

  /**
   * Peek at next damage without removing
   */
  public peek(): ReadyDamage | null {
    if (this.queue.length === 0) return null;

    // Find the one with earliest release time
    let earliest = this.queue[0];
    for (const queued of this.queue) {
      if (queued.releaseTime < earliest.releaseTime) {
        earliest = queued;
      }
    }
    return earliest.damage;
  }

  /**
   * Get time until next damage is ready (ms)
   */
  public getTimeUntilNextReady(currentTime: number = performance.now()): number {
    if (this.queue.length === 0) return Infinity;

    let earliestRelease = Infinity;
    for (const queued of this.queue) {
      if (queued.releaseTime < earliestRelease) {
        earliestRelease = queued.releaseTime;
      }
    }

    return Math.max(0, earliestRelease - currentTime);
  }
}
